import React from 'react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';

/**
 * Small reusable FAQ block that can be embedded on any page.
 * Renders semantic accordion + emits FAQPage JSON-LD via the caller
 * (pass items to `buildFaqJsonLd(items)` from useSEO).
 */
export const PageFAQ = ({ items = [], title = 'Frequently asked', overline = 'Questions', testId = 'page-faq' }) => {
  if (!items.length) return null;
  return (
    <section data-testid={testId} className="py-16 lg:py-24 bg-surface border-t border-brandborder/60">
      <div className="container-luxe max-w-4xl">
        <div className="mb-10">
          <span className="overline">{overline}</span>
          <h2 className="font-display text-3xl lg:text-4xl text-charcoal mt-3 leading-tight">
            {title}
          </h2>
          <div className="mt-4 h-px w-14 bg-gold" />
        </div>

        <Accordion type="single" collapsible className="w-full">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`${testId}-item-${i}`} className="border-brandborder">
              <AccordionTrigger className="font-display text-base md:text-lg text-charcoal hover:text-maroon text-left py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-charcoal/75 leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/** Build FAQPage schema.org JSON-LD from `[{q,a}]` items. */
export const buildFaqJsonLd = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export default PageFAQ;
